import React, { useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { formatShopifyFieldValue, type FormattedValue } from "../../../utils/shopifyFormatting";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
// getSectionTitleTranslation removed (no section header rendered in this widget)
import { ChildTablePreview } from "@/components/child-table/ChildTablePreview";

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Customer Section Widget
 * 
 * Displays customer and channel fields in a 2-column grid layout.
 */
export function CustomerSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  isSaving,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  
  const allFields = useMemo(
    () => section.columns.flatMap((col: any) => col.fields),
    [section.columns]
  );

  const { visibleFieldNames, fieldRequired } = useFieldDependencies(
    allFields,
    formData
  );

  if (section.columns.length === 0 || allFields.length === 0) {
    return null;
  }

  const fieldsToRender = allFields.filter((field) =>
    visibleFieldNames.has(field.fieldname)
  ).filter((field) => {
    // Skip items field and name field
    const isItemsField = (field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect') && field.fieldname === 'items';
    return field.fieldname !== 'name' && !isItemsField;
  });

  if (fieldsToRender.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            CARD,
            !disabled && "cursor-pointer",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => !disabled && setModalOpen(true)}
        >
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              {fieldsToRender.map((field: any, index) => {
                const value = formData[field.fieldname];
                const isTable = field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect';
                const isRequired = fieldRequired[field.fieldname] ?? field.reqd === 1;

                // Special rendering for Table fields: show mini table preview
                let displayValue: React.ReactNode;
                let formattedValue: FormattedValue | null = null;
                if (isTable) {
                  // Use ChildTablePreview for all table fields
                  displayValue = (
                    <ChildTablePreview
                      field={field}
                      value={value}
                      currency={formData.currency || 'THB'}
                      maxRows={3}
                      maxFields={3}
                    />
                  );
                } else {
                  formattedValue = formatShopifyFieldValue(field, value);
                  displayValue = renderFormattedValue(formattedValue);
                }

                // Skip rendering if value is empty
                if (!value && value !== 0 && !isTable) {
                  return null;
                }

                const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
                
                return (
                  <div 
                    key={field.fieldname} 
                    className={cn(
                      "flex flex-col min-w-0",
                      index % 2 === 0 && index < fieldsToRender.length - 1 && "sm:pr-3",
                      index % 2 === 1 && "sm:pl-3"
                    )}
                  >
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {resolvedLabel.label}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words leading-relaxed">
                      {displayValue || <span className="text-gray-400 dark:text-gray-500 italic">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
      />
    </>
  );
}


